import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ScreenReaderAnnounce } from '@/components/ScreenReaderAnnounce'

interface DocumentTitleProviderProps {
  children?: ReactNode
}

/**
 * Creates and provides the document title context so consumers can use
 * the `useDocumentTitle` hook to set the document title. Also announces
 * changes via a live region.
 *
 * @param children
 * @constructor
 */
export function DocumentTitleProvider({
  children,
}: DocumentTitleProviderProps) {
  const [value, set] = useState('')
  const currentTitleRef = useRef('')

  const setTitle = useCallback((v: string) => {
    set(v)
    setDocumentTitle(v)
    currentTitleRef.current = v
  }, [])

  const contextValue: DocumentTitleContextValue = useMemo(
    () => ({
      currentTitleRef,
      value,
      set: setTitle,
    }),
    [value, setTitle],
  )

  return (
    <DocumentTitleContext value={contextValue}>
      <ScreenReaderAnnounce text={contextValue.value} />
      {children}
    </DocumentTitleContext>
  )
}

/**
 * Context for the document title.
 * Used to allow child components to specify the title.
 */
export const DocumentTitleContext = createContext<DocumentTitleContextValue>({
  set: () => void 0,
  value: '',
  currentTitleRef: { current: '' },
})

interface DocumentTitleContextValue {
  value: string
  currentTitleRef: RefObject<string>
  set: (value: string) => unknown
}

/**
 * Sets the document title.
 *
 * @param title
 */
function setDocumentTitle(title: string) {
  if (!title) {
    return
  }
  document.title = title
}
